import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "../context/LanguageContext";

const links = [
  { to: "/", key: "nav_projects" },
  { to: "/about", key: "nav_about" },
  { to: "/contacts", key: "nav_contacts" },
];

const LangToggle = ({ className = "" }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center gap-1 font-mono text-[11px] tracking-[0.2em] ${className}`} data-testid="lang-toggle">
      <button data-testid="lang-pt" onClick={() => setLang("pt")}
        className={`transition-opacity ${lang === "pt" ? "opacity-100" : "opacity-60 hover:opacity-100"}`}>PT</button>
      <span className="opacity-40">/</span>
      <button data-testid="lang-en" onClick={() => setLang("en")}
        className={`transition-opacity ${lang === "en" ? "opacity-100" : "opacity-60 hover:opacity-100"}`}>EN</button>
    </div>
  );
};

export const Nav = () => {
  const { pathname } = useLocation();
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (to) =>
    to === "/" ? pathname === "/" || pathname.startsWith("/project") : pathname === to;

  return (
    <>
      <motion.header
        data-testid="site-header"
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        className="fixed top-0 left-0 right-0 z-[9997] transition-[background-color,backdrop-filter,border-color] duration-500"
        style={{
          backgroundColor: scrolled ? "color-mix(in srgb, var(--vsl-bg) 72%, transparent)" : "transparent",
          backdropFilter: scrolled ? "blur(20px)" : "blur(0px)",
          WebkitBackdropFilter: scrolled ? "blur(20px)" : "blur(0px)",
          borderBottom: scrolled ? "1px solid var(--vsl-border)" : "1px solid transparent",
          color: scrolled ? "var(--vsl-fg)" : "var(--vsl-on-media)",
        }}
      >
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 h-20 flex items-center justify-between">
          <Link to="/" data-testid="brand-logo" className="group flex items-center">
            <img src="/logo-vessel.png" alt="VESSEL" className="h-12 md:h-14 w-auto transition-opacity duration-300 group-hover:opacity-80" />
          </Link>

          <nav className="hidden md:flex items-center gap-10 font-mono text-xs tracking-[0.2em] uppercase">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.key.replace("nav_", "")}`}
                className={`nav-link transition-opacity duration-300 hover:opacity-100 ${
                  isActive(l.to) ? "opacity-100 active" : "opacity-60"
                }`}
              >
                {t(l.key)}
              </Link>
            ))}
            <span className="w-px h-4 bg-[var(--vsl-border)]" />
            <LangToggle />
          </nav>

          <div className="md:hidden flex items-center gap-5">
            <LangToggle />
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={24} strokeWidth={1.4} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[9999] bg-[var(--vsl-bg)] flex flex-col"
          >
            <div className="h-20 px-6 flex items-center justify-between border-b border-[var(--vsl-border)]">
              <img src="/logo-vessel.png" alt="VESSEL" className="h-6 w-auto" />
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} aria-label="Close menu">
                <X size={26} strokeWidth={1.4} />
              </button>
            </div>
            <div className="flex-1 flex flex-col justify-center px-8 gap-2">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${l.key.replace("nav_", "")}`}
                    className="font-display text-6xl leading-tight"
                  >
                    {t(l.key)}
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
